// src/components/Dashboard.js
import React, { useEffect, useState, useContext, useMemo } from 'react';
import { API_URL } from './apiurl';
import { AuthContext } from '../contexts/AuthContext';
import { 
  FiMaximize, 
  FiFilter, 
  FiCalendar, 
  FiDownload, 
  FiRefreshCw,
  FiChevronDown,
  FiChevronUp
} from "react-icons/fi";
import PraemieTabs from './PraemieTabs.js'; // Verwende die neue .js-Version

// Recharts
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  LineChart,
  Line,
  AreaChart,
  Area
} from 'recharts';

// DatePicker etc.
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { format, subDays, startOfMonth, endOfMonth, startOfYear } from 'date-fns';
import de from 'date-fns/locale/de';

// Einheitliche Farbpalette für Charts
const CHART_COLORS = {
  primary: '#3b82f6',   // Blau - Hauptfarbe
  secondary: '#10b981', // Grün - Sekundärfarbe
  accent1: '#f59e0b',   // Orange
  accent2: '#8b5cf6',   // Lila
  accent3: '#ec4899',   // Pink
  negative: '#ef4444',  // Rot für negative Werte
  neutral1: '#6b7280',  // Dunkelgrau
  neutral2: '#d1d5db',  // Hellgrau
  background: '#f9fafb' // Hintergrundfarbe
};

// Farben für das PieChart
const PIE_COLORS = [
  CHART_COLORS.primary,
  CHART_COLORS.accent1, 
  CHART_COLORS.accent2,
  CHART_COLORS.accent3
];

// Hilfsfunktion: Wochentag als Text
const weekdayLabels = {
  0: 'So',
  1: 'Mo',
  2: 'Di',
  3: 'Mi',
  4: 'Do',
  5: 'Fr',
  6: 'Sa',
};

// Custom Date Input Component
const CustomDateInput = React.forwardRef(({ value, onClick, onChange, placeholder }, ref) => (
  <div className="custom-date-input" onClick={onClick}>
    <input
      type="text"
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      ref={ref}
      readOnly
    />
    <FiCalendar className="date-icon" />
  </div>
));

// --- Modal Component ---
const Modal = ({ title, children, onClose }) => (
  <div className="modal-overlay">
    <div className="modal-content">
      <div className="modal-header">
        <h2>{title}</h2>
        <button onClick={onClose} className="close-button">
          <span>&times;</span>
        </button>
      </div>
      <div className="modal-body">
        {children}
      </div>
    </div>
  </div>
);

// --- ChartCard Wrapper für responsive Charts ---
const ChartCard = ({ title, subtitle, children, onEnlarge, isLoading, isEmpty, emptyMessage, icon }) => (
  <div className="chart-card">
    <div className="chart-header">
      <div className="chart-title">
        {icon && <span className="chart-icon">{icon}</span>}
        <div>
          <h3>{title}</h3>
          {subtitle && <p className="chart-subtitle">{subtitle}</p>}
        </div>
      </div>
      {onEnlarge && (
        <button className="enlarge-button" onClick={onEnlarge} title="Vollbild anzeigen">
          <FiMaximize />
        </button>
      )}
    </div>
    <div className="chart-body">
      {isLoading ? (
        <div className="chart-loading">
          <div className="spinner"></div>
          <p>Daten werden geladen...</p>
        </div>
      ) : isEmpty ? (
        <div className="chart-empty">
          <p>{emptyMessage || "Keine Daten verfügbar"}</p>
        </div>
      ) : (
        children
      )}
    </div>
  </div>
);

// --- KPI Card Component ---
const KpiCard = ({ title, value, unit, change, changeLabel, color, icon }) => (
  <div className="kpi-card">
    <div className="kpi-header">
      <h3>{title}</h3>
      {icon && <span className="kpi-icon" style={{ color }}>{icon}</span>}
    </div>
    <div className="kpi-value" style={{ color }}>
      {value}
      {unit && <span className="kpi-unit">{unit}</span>}
    </div>
    {change !== undefined && (
      <div className={`kpi-change ${change >= 0 ? 'positive' : 'negative'}`}>
        {change >= 0 ? '↑' : '↓'} {Math.abs(change)}% {changeLabel}
      </div>
    )}
  </div>
);

// --- Filter Section Component ---
const FilterSection = ({
  customers,
  types,
  selectedCustomer,
  setSelectedCustomer,
  selectedType,
  setSelectedType,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  onReset,
  onApply,
  isFilterExpanded,
  toggleFilterExpanded
}) => {
  // Datum-Presets
  const datePresets = [
    { label: 'Heute', action: () => {
      const today = new Date();
      setStartDate(today);
      setEndDate(today);
    }},
    { label: 'Diese Woche', action: () => {
      const today = new Date();
      const firstDayOfWeek = new Date(today);
      firstDayOfWeek.setDate(today.getDate() - today.getDay() + (today.getDay() === 0 ? -6 : 1));
      setStartDate(firstDayOfWeek);
      setEndDate(today);
    }},
    { label: 'Dieser Monat', action: () => {
      const today = new Date();
      setStartDate(startOfMonth(today));
      setEndDate(today);
    }},
    { label: 'Letzter Monat', action: () => {
      const today = new Date();
      const lastMonth = new Date(today);
      lastMonth.setMonth(today.getMonth() - 1);
      setStartDate(startOfMonth(lastMonth));
      setEndDate(endOfMonth(lastMonth));
    }},
    { label: 'Dieses Jahr', action: () => {
      const today = new Date();
      setStartDate(startOfYear(today));
      setEndDate(today);
    }}
  ];

  return (
    <div className="filter-container">
      <div className="filter-header" onClick={toggleFilterExpanded}>
        <div className="filter-title">
          <FiFilter />
          <h3>Filter</h3>
        </div>
        <button className="filter-toggle">
          {isFilterExpanded ? <FiChevronUp /> : <FiChevronDown />}
        </button>
      </div>
      
      {isFilterExpanded && (
        <div className="filter-body">
          <div className="filter-row">
            <div className="filter-group">
              <label>Kunde:</label>
              <select
                value={selectedCustomer}
                onChange={(e) => setSelectedCustomer(e.target.value)}
              >
                <option value="">Alle Kunden</option>
                {customers.map((customer) => (
                  <option key={customer.id} value={customer.id}>
                    {customer.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label>Arbeitstyp:</label>
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
              >
                <option value="">Alle Typen</option>
                {types.map((type, index) => (
                  <option key={index} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label>Startdatum:</label>
              <DatePicker
                selected={startDate}
                onChange={(date) => setStartDate(date)}
                selectsStart
                startDate={startDate}
                endDate={endDate}
                dateFormat="dd.MM.yyyy"
                locale={de}
                placeholderText="Startdatum"
                customInput={<CustomDateInput />}
              />
            </div>

            <div className="filter-group">
              <label>Enddatum:</label>
              <DatePicker
                selected={endDate}
                onChange={(date) => setEndDate(date)}
                selectsEnd
                startDate={startDate}
                endDate={endDate}
                minDate={startDate}
                dateFormat="dd.MM.yyyy"
                locale={de}
                placeholderText="Enddatum"
                customInput={<CustomDateInput />}
              />
            </div>
          </div>
          
          <div className="filter-presets">
            <label>Schnellauswahl:</label>
            <div className="preset-buttons">
              {datePresets.map((preset, index) => (
                <button 
                  key={index} 
                  onClick={preset.action} 
                  type="button" 
                  className="preset-button"
                >
                  {preset.label}
                </button>
              ))}
            </div>
          </div>
          
          <div className="filter-actions">
            <button onClick={onReset} className="filter-reset-button">
              <FiRefreshCw /> Zurücksetzen
            </button>
            <button onClick={onApply} className="filter-apply-button">
              Filter anwenden
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Formatierungsfunktion für den Chart-Tooltip
const CustomTooltip = ({ active, payload, label, valuePrefix = '', valueSuffix = '' }) => {
  if (active && payload && payload.length) {
    return (
      <div className="custom-tooltip">
        <p className="tooltip-label">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="tooltip-value" style={{ color: entry.color }}>
            {entry.name}: {valuePrefix}{entry.value.toFixed(2)}{valueSuffix}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

function Dashboard() {
  const { auth } = useContext(AuthContext);

  // Daten-States
  const [hoursPerCustomer, setHoursPerCustomer] = useState([]);
  const [absences, setAbsences] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [types, setTypes] = useState([]);
  const [hoursByWeekday, setHoursByWeekday] = useState([]);
  const [monthlyAbsences, setMonthlyAbsences] = useState([]);
  const [praemieData, setPraemieData] = useState([]);
  const [praemieDataLastYear, setPraemieDataLastYear] = useState([]);
  
  // KPI-States (Gesamtzahlen für die KPI-Karten)
  const [kpiData, setKpiData] = useState({
    totalHours: 0,
    totalCustomers: 0,
    averageHoursPerDay: 0,
    absenceRate: 0
  });
  
  // UI-States
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);
  const [isAbsencesEnlarged, setIsAbsencesEnlarged] = useState(false);
  const [isPraemieEnlarged, setIsPraemieEnlarged] = useState(false);
  const [isHoursPerCustomerEnlarged, setIsHoursPerCustomerEnlarged] = useState(false);
  const [isHoursByWeekdayEnlarged, setIsHoursByWeekdayEnlarged] = useState(false);

  // Filter States
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [startDate, setStartDate] = useState(startOfYear(new Date())); // Aktuelles Jahr als Standard
  const [endDate, setEndDate] = useState(new Date());

  // Lade- und Fehlerzustände (pro Datenquelle)
  const [loading, setLoading] = useState({
    hoursPerCustomer: false,
    absences: false,
    customers: false,
    types: false,
    hoursByWeekday: false,
    monthlyAbsences: false,
    praemieData: false,
    kpiData: false
  });
  const [error, setError] = useState({
    hoursPerCustomer: '',
    absences: '',
    customers: '',
    types: '',
    hoursByWeekday: '',
    monthlyAbsences: '',
    praemieData: '',
    kpiData: ''
  });

  // Toggle Filter-Bereich
  const toggleFilterExpanded = () => {
    setIsFilterExpanded(!isFilterExpanded);
  };

  // Erstes Laden
  useEffect(() => {
    const initializeData = async () => {
      await fetchCustomers();
      await fetchTypes();
      // Nachdem Kunden und Typen geladen wurden, alle Daten laden
      fetchAllData();
    };
    
    initializeData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Automatisches Laden der Daten nach Änderung des Filters
  useEffect(() => {
    if (auth?.token) {
      fetchAllData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCustomer, selectedType, startDate, endDate]);

  // Fetch-Funktion für alle Daten, die bei Filter-Anwendung neu geladen werden
  const fetchAllData = () => {
    fetchHoursPerCustomer();
    fetchAbsences();
    fetchHoursByWeekday();
    fetchMonthlyAbsences();
    fetchPraemieData();
    fetchPraemieDataLastYear();
    fetchKpiData();
  };

  // Filter anwenden
  const handleApplyFilters = () => {
    fetchAllData();
  };

  // Filter zurücksetzen
  const handleResetFilters = () => {
    setSelectedCustomer('');
    setSelectedType('');
    setStartDate(startOfMonth(new Date()));
    setEndDate(new Date());
    // Nach kurzer Verzögerung Daten neu laden
    setTimeout(() => {
      fetchAllData();
    }, 100);
  };

  // --- Fetch Funktionen ---
  const fetchCustomers = async () => {
    setLoading(prev => ({ ...prev, customers: true }));
    setError(prev => ({ ...prev, customers: '' }));
    
    try {
      const response = await fetch(`${API_URL}/customersstats`, {
        headers: { 'Authorization': `Bearer ${auth.token}` },
      });
      
      if (!response.ok) throw new Error('Fehler beim Abrufen der Kunden');
      
      const data = await response.json();
      setCustomers(data);
    } catch (error) {
      //console.error('Fehler beim Laden der Kundendaten:', error);
      setError(prev => ({ ...prev, customers: error.message }));
    } finally {
      setLoading(prev => ({ ...prev, customers: false }));
    }
  };

  const fetchTypes = async () => {
    setLoading(prev => ({ ...prev, types: true }));
    setError(prev => ({ ...prev, types: '' }));
    
    try {
      const response = await fetch(`${API_URL}/types`, {
        headers: { 'Authorization': `Bearer ${auth.token}` },
      });
      
      if (!response.ok) throw new Error('Fehler beim Abrufen der Arbeitstypen');
      
      const data = await response.json();
      setTypes(data);
    } catch (error) {
      //console.error('Fehler beim Laden der Arbeitstypen:', error);
      setError(prev => ({ ...prev, types: error.message }));
    } finally {
      setLoading(prev => ({ ...prev, types: false }));
    }
  };

  const fetchHoursPerCustomer = async () => {
    setLoading(prev => ({ ...prev, hoursPerCustomer: true }));
    setError(prev => ({ ...prev, hoursPerCustomer: '' }));
    
    try {
      const response = await fetch(`${API_URL}/hourspercustomer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.token}`,
        },
        body: JSON.stringify({
          selectedCustomer,
          startDate: startDate ? format(startDate, 'yyyy-MM-dd') : '',
          endDate: endDate ? format(endDate, 'yyyy-MM-dd') : '',
          selectedType,
        }),
      });
      
      if (!response.ok)
        throw new Error('Fehler beim Abrufen der Stunden pro Kunde');
      
      const data = await response.json();
      setHoursPerCustomer(data);
    } catch (error) {
      //console.error('Fehler beim Laden der Stunden pro Kunde:', error);
      setError(prev => ({ ...prev, hoursPerCustomer: error.message }));
    } finally {
      setLoading(prev => ({ ...prev, hoursPerCustomer: false }));
    }
  };

  const fetchAbsences = async () => {
    setLoading(prev => ({ ...prev, absences: true }));
    setError(prev => ({ ...prev, absences: '' }));
    
    try {
      const response = await fetch(`${API_URL}/absences`, {
        headers: { 'Authorization': `Bearer ${auth.token}` },
      });
      
      if (!response.ok) throw new Error('Fehler beim Abrufen der Abwesenheiten');
      
      const data = await response.json();
      setAbsences(data);
    } catch (error) {
      //console.error('Fehler beim Laden der Abwesenheiten:', error);
      setError(prev => ({ ...prev, absences: error.message }));
    } finally {
      setLoading(prev => ({ ...prev, absences: false }));
    }
  };

  const fetchHoursByWeekday = async () => {
    setLoading(prev => ({ ...prev, hoursByWeekday: true }));
    setError(prev => ({ ...prev, hoursByWeekday: '' }));
    
    try {
      const response = await fetch(`${API_URL}/hours-by-weekday`, {
        headers: { 'Authorization': `Bearer ${auth.token}` },
      });
      
      if (!response.ok) throw new Error('Fehler bei "hours-by-weekday"');
      
      const data = await response.json();
      const mapped = data.map(item => ({
        weekday: weekdayLabels[item.weekday] || 'Tag?',
        hours: item.total,
      }));
      setHoursByWeekday(mapped);
    } catch (error) {
      //console.error('Fehler beim Laden der Stunden pro Wochentag:', error);
      setError(prev => ({ ...prev, hoursByWeekday: error.message }));
    } finally {
      setLoading(prev => ({ ...prev, hoursByWeekday: false }));
    }
  };

  const fetchMonthlyAbsences = async () => {
    setLoading(prev => ({ ...prev, monthlyAbsences: true }));
    setError(prev => ({ ...prev, monthlyAbsences: '' }));
    
    try {
      const response = await fetch(`${API_URL}/monthly-absences`, {
        headers: { 'Authorization': `Bearer ${auth.token}` },
      });
      
      if (!response.ok) throw new Error('Fehler bei "monthly-absences"');
      
      const data = await response.json();
      setMonthlyAbsences(data);
    } catch (error) {
      //console.error('Fehler beim Laden der monatlichen Abwesenheiten:', error);
      setError(prev => ({ ...prev, monthlyAbsences: error.message }));
    } finally {
      setLoading(prev => ({ ...prev, monthlyAbsences: false }));
    }
  };

  const fetchPraemieData = async () => {
    setLoading(prev => ({ ...prev, praemieData: true }));
    setError(prev => ({ ...prev, praemieData: '' }));
    
    try {
      const response = await fetch(`${API_URL}/praemie-data`, {
        headers: { 'Authorization': `Bearer ${auth.token}` },
      });
      
      if (!response.ok)
        throw new Error('Fehler beim Abrufen der Prämien-Daten');
      
      const data = await response.json();
      //console.log("Prämiendaten geladen:", data); // Debug-Ausgabe
      setPraemieData(data);
    } catch (error) {
      //console.error('Fehler beim Laden der Prämien-Daten:', error);
      setError(prev => ({ ...prev, praemieData: error.message }));
    } finally {
      setLoading(prev => ({ ...prev, praemieData: false }));
    }
  };
  
  const fetchPraemieDataLastYear = async () => {
    try {
      const response = await fetch(`${API_URL}/praemie-data-lastyear`, {
        headers: { 'Authorization': `Bearer ${auth.token}` },
      });
      
      if (!response.ok)
        throw new Error('Fehler beim Abrufen der Prämien-Daten (Vorjahr)');
      
      const data = await response.json();
      //console.log("Prämiendaten Vorjahr geladen:", data); // Debug-Ausgabe
      setPraemieDataLastYear(data);
    } catch (error) {
      //console.error('Fehler beim Laden der Prämien-Daten (Vorjahr):', error);
    }
  };

  const fetchKpiData = async () => {
    setLoading(prev => ({ ...prev, kpiData: true }));
    setError(prev => ({ ...prev, kpiData: '' }));
    
    try {
      // Gesamtstunden berechnen
      const totalHours = hoursPerCustomer.reduce((sum, item) => sum + item.hours, 0);
      
      // Aktive Kunden zählen anhand des customerName-Felds
      // Da die Backend-Abfrage nach customerId gruppiert, ist jeder Eintrag ein eindeutiger Kunde
      const activeCustomers = hoursPerCustomer.length;
      
      // Alternative: Falls sich die Datenstruktur ändert und customerName verfügbar ist
      // const uniqueCustomerNames = new Set(hoursPerCustomer.map(item => item.customerName));
      // const activeCustomers = uniqueCustomerNames.size;
      
      // Durchschnittliche Stunden pro Tag berechnen
      const daysInPeriod = startDate && endDate ? 
        Math.max(1, Math.round((endDate - startDate) / (1000 * 60 * 60 * 24))) : 30;
      const avgHoursPerDay = totalHours / daysInPeriod;
      
      // Abwesenheitsrate berechnen
      const totalAbsenceDays = absences.reduce((sum, item) => sum + item.value, 0);
      const absenceRate = totalAbsenceDays > 0 ? 
        (totalAbsenceDays / (totalAbsenceDays + totalHours)) * 100 : 0;
      
      setKpiData({
        totalHours,
        totalCustomers: activeCustomers,
        averageHoursPerDay: avgHoursPerDay,
        absenceRate
      });
    } catch (error) {
      //console.error('Fehler bei der Berechnung der KPI-Daten:', error);
      setError(prev => ({ ...prev, kpiData: error.message }));
    } finally {
      setLoading(prev => ({ ...prev, kpiData: false }));
    }
  };

  // Hilfsfunktion: Transformiere monthlyAbsences in ein "stacked" Format
  const buildStackedData = () => {
    const byPeriod = {};
    monthlyAbsences.forEach(item => {
      const key = item.period;
      if (!byPeriod[key]) byPeriod[key] = { period: key };
      byPeriod[key][item.type] = item.count;
    });
    return Object.values(byPeriod);
  };

  const stackedData = buildStackedData();

  // Formatierte Datumsanzeige für den ausgewählten Zeitraum
  const dateRangeDisplay = useMemo(() => {
    if (!startDate || !endDate) return '';
    
    const formatDate = (date) => format(date, 'dd.MM.yyyy');
    return `${formatDate(startDate)} - ${formatDate(endDate)}`;
  }, [startDate, endDate]);

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h2>Dashboard</h2>
        <div className="date-range-display">
          <FiCalendar /> {dateRangeDisplay}
        </div>
      </div>
      
      <FilterSection
        customers={customers}
        types={types}
        selectedCustomer={selectedCustomer}
        setSelectedCustomer={setSelectedCustomer}
        selectedType={selectedType}
        setSelectedType={setSelectedType}
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        onReset={handleResetFilters}
        onApply={handleApplyFilters}
        isFilterExpanded={isFilterExpanded}
        toggleFilterExpanded={toggleFilterExpanded}
      />
      
      {/* KPI Cards */}
      <div className="kpi-container">
        <KpiCard 
          title="Gesamtstunden" 
          value={kpiData.totalHours.toFixed(1)} 
          unit="h"
          icon="⏱️"
          color={CHART_COLORS.primary}
        />
        <KpiCard 
          title="Aktive Kunden" 
          value={kpiData.totalCustomers} 
          icon="👥"
          color={CHART_COLORS.accent1}
        />
        <KpiCard 
          title="Ø Stunden/Tag" 
          value={kpiData.averageHoursPerDay.toFixed(1)} 
          unit="h"
          icon="📊"
          color={CHART_COLORS.accent2}
        />
        <KpiCard 
          title="Abwesenheitsrate" 
          value={kpiData.absenceRate.toFixed(1)} 
          unit="%"
          icon="🏝️"
          color={CHART_COLORS.accent3}
        />
      </div>
      
      {/* Charts Grid */}
      <div className="charts-grid">
        {/* Stunden pro Kunde */}
        <ChartCard 
          title="Stunden pro Kunde" 
          subtitle={dateRangeDisplay}
          icon="⏱️"
          onEnlarge={() => setIsHoursPerCustomerEnlarged(true)}
          isLoading={loading.hoursPerCustomer}
          isEmpty={hoursPerCustomer.length === 0}
        >
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={hoursPerCustomer}>
              <CartesianGrid strokeDasharray="3 3" stroke={CHART_COLORS.neutral2} />
              <XAxis 
                dataKey="customerName" 
                tick={{ fill: CHART_COLORS.neutral1 }}
                tickLine={{ stroke: CHART_COLORS.neutral2 }}
              />
              <YAxis 
                tick={{ fill: CHART_COLORS.neutral1 }}
                tickLine={{ stroke: CHART_COLORS.neutral2 }}
              />
              <Tooltip content={<CustomTooltip valueSuffix=" h" />} />
              <Legend />
              <Bar 
                dataKey="hours" 
                fill={CHART_COLORS.primary} 
                name="Stunden" 
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Stunden pro Wochentag */}
        <ChartCard 
          title="Stunden pro Wochentag" 
          subtitle="Arbeitsverteilung nach Wochentagen"
          icon="📅"
          onEnlarge={() => setIsHoursByWeekdayEnlarged(true)}
          isLoading={loading.hoursByWeekday}
          isEmpty={hoursByWeekday.length === 0}
        >
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={hoursByWeekday}>
              <CartesianGrid strokeDasharray="3 3" stroke={CHART_COLORS.neutral2} />
              <XAxis 
                dataKey="weekday" 
                tick={{ fill: CHART_COLORS.neutral1 }}
              />
              <YAxis 
                tick={{ fill: CHART_COLORS.neutral1 }}
              />
              <Tooltip content={<CustomTooltip valueSuffix=" h" />} />
              <Legend />
              <Bar 
                dataKey="hours" 
                fill={CHART_COLORS.accent2} 
                name="Stunden" 
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Abwesenheiten (PieChart) */}
        <ChartCard 
          title="Abwesenheiten" 
          subtitle="Verteilung nach Abwesenheitstyp"
          icon="🏝️"
          isLoading={loading.absences}
          isEmpty={absences.length === 0}
        >
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={absences}
                dataKey="value"
                nameKey="type"
                cx="50%"
                cy="50%"
                outerRadius={100}
                fill={CHART_COLORS.primary}
                label={({ type, value }) => `${type}: ${value}`}
              >
                {absences.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={PIE_COLORS[index % PIE_COLORS.length]}
                  />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Abwesenheiten pro Monat (Stacked Bar) */}
        <ChartCard
          title="Abwesenheiten pro Monat"
          subtitle="Monatsübersicht nach Kategorien"
          icon="📊"
          onEnlarge={() => setIsAbsencesEnlarged(true)}
          isLoading={loading.monthlyAbsences}
          isEmpty={stackedData.length === 0}
        >
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={stackedData}>
              <CartesianGrid strokeDasharray="3 3" stroke={CHART_COLORS.neutral2} />
              <XAxis 
                dataKey="period" 
                tick={{ fill: CHART_COLORS.neutral1 }}
              />
              <YAxis 
                tick={{ fill: CHART_COLORS.neutral1 }}
              />
              <Tooltip />
              <Legend />
              <Bar 
                dataKey="krank" 
                stackId="stack" 
                fill={CHART_COLORS.negative} 
                name="Krank" 
              />
              <Bar 
                dataKey="urlaub" 
                stackId="stack" 
                fill={CHART_COLORS.accent1} 
                name="Urlaub" 
              />
              <Bar 
                dataKey="feiertag" 
                stackId="stack" 
                fill={CHART_COLORS.accent2} 
                name="Feiertag" 
              />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
      
      {/* Prämien Übersicht */}
      <div className="praemie-container">
        <ChartCard
          title="Prämienübersicht"
          subtitle="Aktueller Stand und Prognosen"
          icon="💰"
          onEnlarge={() => setIsPraemieEnlarged(true)}
          isLoading={loading.praemieData}
          isEmpty={!praemieData}
        >
          <PraemieTabs 
            praemieData={praemieData} 
            praemieDataLastYear={praemieDataLastYear} 
            styles={{
              tablex: "data-table",
              tdx: "data-table-cell",
              chartx: "chart-card",
              chartHeaderx: "chart-header",
              enlargeButtonx: "enlarge-button"
            }}
          />
        </ChartCard>
      </div>

      {/* Modals für detaillierte Ansichten */}
      {isHoursPerCustomerEnlarged && (
        <Modal title="Stunden pro Kunde (Detailansicht)" onClose={() => setIsHoursPerCustomerEnlarged(false)}>
          <ResponsiveContainer width="100%" height={500}>
            <BarChart data={hoursPerCustomer}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="customerName" />
              <YAxis />
              <Tooltip content={<CustomTooltip valueSuffix=" h" />} />
              <Legend />
              <Bar dataKey="hours" fill={CHART_COLORS.primary} name="Stunden" />
            </BarChart>
          </ResponsiveContainer>
          
          <div className="modal-table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Kunde</th>
                  <th>Stunden</th>
                  <th>Anteil (%)</th>
                </tr>
              </thead>
              <tbody>
                {hoursPerCustomer.map((item, index) => {
                  const totalHours = hoursPerCustomer.reduce((sum, i) => sum + i.hours, 0);
                  const percentage = totalHours > 0 ? (item.hours / totalHours) * 100 : 0;
                  
                  return (
                    <tr key={index}>
                      <td>{item.customerName}</td>
                      <td>{item.hours.toFixed(2)} h</td>
                      <td>{percentage.toFixed(2)}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Modal>
      )}

      {isHoursByWeekdayEnlarged && (
        <Modal title="Stunden pro Wochentag (Detailansicht)" onClose={() => setIsHoursByWeekdayEnlarged(false)}>
          <ResponsiveContainer width="100%" height={500}>
            <BarChart data={hoursByWeekday}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="weekday" />
              <YAxis />
              <Tooltip content={<CustomTooltip valueSuffix=" h" />} />
              <Legend />
              <Bar dataKey="hours" fill={CHART_COLORS.accent2} name="Stunden" />
            </BarChart>
          </ResponsiveContainer>
          
          <div className="modal-table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Wochentag</th>
                  <th>Stunden</th>
                  <th>Anteil (%)</th>
                </tr>
              </thead>
              <tbody>
                {hoursByWeekday.map((item, index) => {
                  const totalHours = hoursByWeekday.reduce((sum, i) => sum + i.hours, 0);
                  const percentage = totalHours > 0 ? (item.hours / totalHours) * 100 : 0;
                  
                  return (
                    <tr key={index}>
                      <td>{item.weekday}</td>
                      <td>{item.hours.toFixed(2)} h</td>
                      <td>{percentage.toFixed(2)}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Modal>
      )}
      
      {isAbsencesEnlarged && (
        <Modal title="Abwesenheiten pro Monat (Detailansicht)" onClose={() => setIsAbsencesEnlarged(false)}>
          <ResponsiveContainer width="100%" height={500}>
            <BarChart data={stackedData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="period" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="krank" stackId="stack" fill={CHART_COLORS.negative} name="Krank" />
              <Bar dataKey="urlaub" stackId="stack" fill={CHART_COLORS.accent1} name="Urlaub" />
              <Bar dataKey="feiertag" stackId="stack" fill={CHART_COLORS.accent2} name="Feiertag" />
            </BarChart>
          </ResponsiveContainer>
          
          <div className="modal-table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Zeitraum</th>
                  <th>Krank</th>
                  <th>Urlaub</th>
                  <th>Feiertag</th>
                  <th>Gesamt</th>
                </tr>
              </thead>
              <tbody>
                {stackedData.map((item, index) => {
                  const krank = item.krank || 0;
                  const urlaub = item.urlaub || 0;
                  const feiertag = item.feiertag || 0;
                  const total = krank + urlaub + feiertag;
                  
                  return (
                    <tr key={index}>
                      <td>{item.period}</td>
                      <td>{krank}</td>
                      <td>{urlaub}</td>
                      <td>{feiertag}</td>
                      <td><strong>{total}</strong></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Modal>
      )}

      {/* Prämienübersicht Modal */}
      {isPraemieEnlarged && (
        <Modal title="Prämienübersicht (Detailansicht)" onClose={() => setIsPraemieEnlarged(false)}>
          <div className="modal-praemie-content">
            <PraemieTabs 
              praemieData={praemieData} 
              praemieDataLastYear={praemieDataLastYear} 
              styles={{
                tablex: "data-table",
                tdx: "data-table-cell",
                chartx: "chart-card",
                chartHeaderx: "chart-header",
                enlargeButtonx: "enlarge-button"
              }}
            />
          </div>
        </Modal>
      )}

      <style jsx>{`
        /* Dashboard Layout */
        .dashboard {
          padding: 2rem;
          background-color: #f9fafb;
          min-height: 100vh;
        }
        
        .dashboard-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1.5rem;
        }
        
        .dashboard-header h2 {
          font-size: 1.8rem;
          color: #111827;
          margin: 0;
        }
        
        .date-range-display {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 1rem;
          background-color: white;
          border-radius: 8px;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
          font-size: 0.9rem;
          color: #4b5563;
        }
        
        /* KPI Cards */
        .kpi-container {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 1rem;
          margin-bottom: 2rem;
        }
        
        .kpi-card {
          background-color: white;
          padding: 1.5rem;
          border-radius: 8px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          display: flex;
          flex-direction: column;
        }
        
        .kpi-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.5rem;
        }
        
        .kpi-header h3 {
          font-size: 1rem;
          color: #6b7280;
          margin: 0;
          font-weight: 500;
        }
        
        .kpi-icon {
          font-size: 1.5rem;
        }
        
        .kpi-value {
          font-size: 2rem;
          font-weight: 700;
          margin: 0.5rem 0;
          display: flex;
          align-items: baseline;
        }
        
        .kpi-unit {
          font-size: 1rem;
          margin-left: 0.25rem;
          color: #6b7280;
          font-weight: 500;
        }
        
        .kpi-change {
          font-size: 0.85rem;
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 0.25rem;
        }
        
        .kpi-change.positive {
          color: #10b981;
        }
        
        .kpi-change.negative {
          color: #ef4444;
        }
        
        /* Filter Section */
        .filter-container {
          background-color: white;
          border-radius: 8px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          margin-bottom: 2rem;
          overflow: hidden;
        }
        
        .filter-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem 1.5rem;
          cursor: pointer;
          border-bottom: ${isFilterExpanded ? '1px solid #e5e7eb' : 'none'};
        }
        
        .filter-title {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-weight: 500;
        }
        
        .filter-title h3 {
          margin: 0;
          font-size: 1.1rem;
          color: #111827;
        }
        
        .filter-toggle {
          background: none;
          border: none;
          color: #6b7280;
          cursor: pointer;
          font-size: 1.2rem;
          padding: 0.25rem;
        }
        
        .filter-body {
          padding: 1.5rem;
        }
        
        .filter-row {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1rem;
          margin-bottom: 1.5rem;
        }
        
        .filter-group {
          display: flex;
          flex-direction: column;
        }
        
        .filter-group label {
          font-size: 0.9rem;
          margin-bottom: 0.5rem;
          color: #4b5563;
          font-weight: 500;
        }
        
        .filter-group select,
        .filter-group input {
          padding: 0.6rem;
          font-size: 1rem;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          background-color: white;
        }
        
        .custom-date-input {
          position: relative;
          display: flex;
          align-items: center;
        }
        
        .custom-date-input input {
          width: 100%;
          padding-right: 2.5rem;
        }
        
        .date-icon {
          position: absolute;
          right: 0.75rem;
          color: #6b7280;
          pointer-events: none;
        }
        
        .filter-presets {
          margin-bottom: 1.5rem;
        }
        
        .filter-presets label {
          font-size: 0.9rem;
          margin-bottom: 0.5rem;
          color: #4b5563;
          font-weight: 500;
          display: block;
        }
        
        .preset-buttons {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
        }
        
        .preset-button {
          background-color: #f3f4f6;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          padding: 0.5rem 0.75rem;
          font-size: 0.85rem;
          color: #374151;
          cursor: pointer;
          transition: all 0.2s;
        }
        
        .preset-button:hover {
          background-color: #e5e7eb;
          border-color: #9ca3af;
        }
        
        .filter-actions {
          display: flex;
          justify-content: flex-end;
          gap: 1rem;
        }
        
        .filter-reset-button {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.6rem 1rem;
          background-color: #f3f4f6;
          color: #4b5563;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 0.9rem;
          cursor: pointer;
        }
        
        .filter-apply-button {
          padding: 0.6rem 1.25rem;
          background-color: #3b82f6;
          color: white;
          border: none;
          border-radius: 6px;
          font-size: 0.9rem;
          cursor: pointer;
          transition: background-color 0.2s;
        }
        
        .filter-apply-button:hover {
          background-color: #2563eb;
        }
        
        /* Charts Grid */
        .charts-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(500px, 1fr));
          gap: 1.5rem;
          margin-bottom: 2rem;
        }
        
        @media (max-width: 1024px) {
          .charts-grid {
            grid-template-columns: 1fr;
          }
        }
        
        .chart-card {
          background-color: white;
          border-radius: 8px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          padding: 1.5rem;
          display: flex;
          flex-direction: column;
        }
        
        .chart-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 1rem;
        }
        
        .chart-title {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }
        
        .chart-title h3 {
          margin: 0;
          font-size: 1.1rem;
          color: #111827;
        }
        
        .chart-subtitle {
          margin: 0.25rem 0 0 0;
          font-size: 0.85rem;
          color: #6b7280;
        }
        
        .chart-icon {
          font-size: 1.5rem;
        }
        
        .enlarge-button {
          background: none;
          border: none;
          color: #6b7280;
          cursor: pointer;
          font-size: 1.2rem;
          padding: 0.25rem;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .enlarge-button:hover {
          color: #3b82f6;
        }
        
        .chart-body {
          flex: 1;
          display: flex;
          flex-direction: column;
          justify-content: center;
        }
        
        .chart-loading {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 3rem;
          color: #6b7280;
        }
        
        .spinner {
          width: 2.5rem;
          height: 2.5rem;
          border: 3px solid rgba(59, 130, 246, 0.2);
          border-radius: 50%;
          border-top-color: #3b82f6;
          animation: spin 1s linear infinite;
          margin-bottom: 1rem;
        }
        
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        
        .chart-empty {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 3rem;
          color: #6b7280;
          text-align: center;
          background-color: #f9fafb;
          border-radius: 6px;
        }
        
        /* Prämie Container */
        .praemie-container {
          margin-bottom: 2rem;
        }
        
        /* Modal */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.5);
          z-index: 9999;
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 1rem;
        }
        
        .modal-content {
          background-color: white;
          border-radius: 8px;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.08);
          width: 90%;
          max-width: 1200px;
          max-height: 90vh;
          display: flex;
          flex-direction: column;
        }
        
        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1.25rem 1.5rem;
          border-bottom: 1px solid #e5e7eb;
        }
        
        .modal-header h2 {
          margin: 0;
          font-size: 1.25rem;
          color: #111827;
        }
        
        .close-button {
          background: none;
          border: none;
          font-size: 1.5rem;
          line-height: 1;
          color: #6b7280;
          cursor: pointer;
        }
        
        .modal-body {
          padding: 1.5rem;
          overflow-y: auto;
          flex: 1;
        }
        
        .modal-table-container {
          margin-top: 2rem;
          border-top: 1px solid #e5e7eb;
          padding-top: 1.5rem;
        }
        
        .data-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.95rem;
        }
        
        .data-table th {
          background-color: #f9fafb;
          padding: 0.75rem 1rem;
          text-align: left;
          font-weight: 500;
          color: #4b5563;
          border-bottom: 1px solid #e5e7eb;
        }
        
        .data-table td {
          padding: 0.75rem 1rem;
          border-bottom: 1px solid #e5e7eb;
          color: #111827;
        }
        
        .data-table tr:last-child td {
          border-bottom: none;
        }
        
        /* Tooltip */
        .custom-tooltip {
          background-color: rgba(255, 255, 255, 0.95);
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          padding: 0.75rem;
          box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
        }
        
        .tooltip-label {
          margin: 0 0 0.5rem 0;
          font-weight: 500;
          color: #111827;
        }
        
        .tooltip-value {
          margin: 0.25rem 0;
          font-size: 0.9rem;
        }
        
        .modal-praemie-content {
          display: flex;
          flex-direction: column;
          gap: 2rem;
        }
      `}</style>
    </div>
  );
}

export default Dashboard;